import React, { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { cases as casesApi, errorMessage } from '../utils/api';

/**
 * لینک اختصاصی بیمار، QR آن، و راه‌های بیرون بردنشان.
 *
 * تصویربردار لینک را کپی می‌کند و روی فایل کلیشه می‌گذارد، یا QR را
 * به‌صورت PNG می‌گیرد و همان‌جا جاسازی می‌کند. لینک برای پرونده‌های آمده از
 * پرتو خودکار ساخته می‌شود؛ پرونده‌ی ثبت‌شده‌ی دستی شناسه‌ی پرتو ندارد و
 * باید دستی وارد شود.
 */

const SOURCE_LABEL = {
  parto: 'ساخته‌شده از شناسه‌ی پذیرش پرتو',
  manual: 'دستی وارد شده',
};

export default function PatientLink({ item, onChanged, editable = false }) {
  const [editing, setEditing] = useState(false);

  if (!item.patient_link && !editable) return null;

  return (
    <div className="plink">
      <div className="plink__head">
        <span className="plink__title">🔗 لینک اختصاصی بیمار</span>
        {item.patient_link_source && (
          <span className="plink__source">{SOURCE_LABEL[item.patient_link_source]}</span>
        )}
      </div>

      {item.patient_link ? (
        <>
          <div className="plink__row">
            <code className="plink__url" dir="ltr" title={item.patient_link}>
              {item.patient_link}
            </code>
            <CopyButton text={item.patient_link} />
          </div>

          <QrPanel caseId={item.id} orderNumber={item.order_number} />

          {editable && (
            <button
              type="button"
              className="link plink__edit"
              onClick={() => setEditing((value) => !value)}
            >
              {editing ? 'انصراف' : 'تغییر دستی لینک'}
            </button>
          )}
        </>
      ) : (
        <p className="plink__empty">
          این پرونده شناسه‌ی پذیرش پرتو ندارد — احتمالاً دستی ثبت شده. لینکش
          را از پرتو بردارید و اینجا بگذارید.
        </p>
      )}

      {(editing || (editable && !item.patient_link)) && (
        <LinkForm
          caseId={item.id}
          current={item.patient_link_source === 'manual' ? item.patient_link : ''}
          onDone={() => {
            setEditing(false);
            if (onChanged) onChanged();
          }}
        />
      )}
    </div>
  );
}

function CopyButton({ text }) {
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!copied) return undefined;
    const timer = setTimeout(() => setCopied(false), 2000);
    return () => clearTimeout(timer);
  }, [copied]);

  const copy = async () => {
    // دو مسیر، و دومی فقط برای «نبودنِ» اولی نیست:
    //
    //   ۱. navigator.clipboard روی HTTPS و localhost هست. روی
    //      flowtrack.ir مشکلی نیست، ولی اگر کسی با نشانی IP وارد شود
    //      اصلاً وجود ندارد.
    //   ۲. حتی وقتی هست، ممکن است پرتاب کند — مثلاً وقتی پنجره فوکوس
    //      ندارد. پس شکستش هم باید به مسیر دوم برود، نه مستقیم به خطا.
    if (navigator.clipboard && window.isSecureContext) {
      try {
        await navigator.clipboard.writeText(text);
        setCopied(true);
        return;
      } catch (err) {
        // می‌افتیم روی مسیر دوم
      }
    }

    try {
      legacyCopy(text);
      setCopied(true);
    } catch (err) {
      toast.error('کپی نشد. لینک را دستی انتخاب و کپی کنید.');
    }
  };

  return (
    <button type="button" className="btn btn--ghost btn--sm plink__copy" onClick={copy}>
      {copied ? '✓ کپی شد' : '📋 کپی لینک'}
    </button>
  );
}

/** مسیر دوم کپی، برای وقتی که صفحه امن شمرده نمی‌شود. */
function legacyCopy(text) {
  const field = document.createElement('textarea');
  field.value = text;
  field.setAttribute('readonly', '');
  field.style.position = 'fixed';
  field.style.opacity = '0';
  document.body.appendChild(field);
  field.select();
  const ok = document.execCommand('copy');
  field.remove();
  if (!ok) throw new Error('copy failed');
}

function QrPanel({ caseId, orderNumber }) {
  const [src, setSrc] = useState(null);
  const [failed, setFailed] = useState(false);

  // QR توکن می‌خواهد، پس <img src> مستقیم کار نمی‌کند؛ blob می‌گیریم.
  useEffect(() => {
    let url = null;
    let alive = true;

    casesApi
      .patientQr(caseId)
      .then(({ data }) => {
        if (!alive) return;
        url = window.URL.createObjectURL(data);
        setSrc(url);
      })
      .catch(() => {
        if (alive) setFailed(true);
      });

    return () => {
      alive = false;
      if (url) window.URL.revokeObjectURL(url);
    };
  }, [caseId]);

  const download = async () => {
    try {
      const { data } = await casesApi.patientQr(caseId, 'png');
      const url = window.URL.createObjectURL(data);
      const link = document.createElement('a');
      link.href = url;
      link.download = `qr-${orderNumber || caseId}.png`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      toast.error(errorMessage(err, 'تصویر QR دریافت نشد.'));
    }
  };

  if (failed) return null;

  return (
    <div className="plink__qr">
      {src ? (
        <img src={src} alt="QR لینک اختصاصی بیمار" width="104" height="104" />
      ) : (
        <div className="plink__qr-blank" aria-hidden="true" />
      )}
      <div className="plink__qr-side">
        <p className="plink__hint">
          روی برگه‌ی گزارش چاپ می‌شود. برای فایل کلیشه، تصویرش را بگیرید.
        </p>
        <button type="button" className="btn btn--ghost btn--sm" onClick={download}>
          ⬇️ دریافت تصویر QR
        </button>
      </div>
    </div>
  );
}

function LinkForm({ caseId, current, onDone }) {
  const [value, setValue] = useState(current || '');
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      await casesApi.setPatientLink(caseId, value.trim());
      toast.success(value.trim() ? 'لینک ثبت شد.' : 'لینک دستی برداشته شد.');
      onDone();
    } catch (err) {
      toast.error(errorMessage(err, 'لینک ثبت نشد.'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="plink__form">
      <input
        type="url"
        dir="ltr"
        placeholder="http://i.partoapp.ir/PV/?i=…"
        value={value}
        onChange={(event) => setValue(event.target.value)}
      />
      <button type="button" className="btn btn--primary btn--sm" disabled={saving} onClick={save}>
        {saving ? '…' : 'ثبت'}
      </button>
      {current && (
        <button
          type="button"
          className="link"
          disabled={saving}
          onClick={() => {
            setValue('');
            casesApi
              .setPatientLink(caseId, '')
              .then(() => {
                toast.success('لینک دستی برداشته شد.');
                onDone();
              })
              .catch((err) => toast.error(errorMessage(err, 'انجام نشد.')));
          }}
        >
          برگرد به لینک خودکار
        </button>
      )}
    </div>
  );
}
